import { useState } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";
import type { z } from "zod";

import { IsoDateSchema, type IsoDate } from "@thalamos/common";
import { today, type UserId } from "@thalamos/haven-common";

import { CancelOrProceedActions } from "../CancelOrProceedActions/CancelOrProceedActions.js";

/** How a submission went: recorded, refused by the server, or not reached. */
export type SubmitOutcome = "submitted" | "refused" | "failed";

/** Who a definitive assessment goes to for approval. */
export type Approver = { userId: UserId; name: string };

/** What sending an assessment on states. */
export type Submission = {
  /** The day the answers describe the young person as of. */
  assessedOn: IsoDate;
  /** Who the dialog said a definitive assessment goes to. */
  approverUserId?: UserId | undefined;
};

/** The items a guest is about to send as not answered, of every item asked, and how many in each domain that has any. */
export type ItemsNotAnswered = {
  count: number;
  of: number;
  byDomain: readonly { group: string; title: string; count: number }[];
};

/**
 * What sending an assessment on does: a definitive one goes for approval, any
 * other is completed by its assessor, and a guest's is sent to whoever asked
 * for it, which ends the guest's access, with a warning first where items are
 * left unanswered.
 */
export type SubmitTrack =
  | {
      kind: "approval";
      /** Whoever approves the owner's work now; undefined while nobody does. */
      approver: Approver | undefined;
      /** Who runs the case, who can add an Approver; undefined where nobody does. */
      caseManagerName: string | undefined;
    }
  | { kind: "completion"; personName: string }
  | { kind: "to_asker"; askerName: string; notAnswered: ItemsNotAnswered };

/**
 * The message key the day a submission describes is refused with, or
 * `undefined` for a real calendar day no later than today.
 */
const assessedOnError = (
  parsed: z.ZodSafeParseResult<IsoDate>,
): string | undefined => {
  if (!parsed.success) {
    return "assessment.assessedOn.invalid";
  }

  return parsed.data > today()
    ? "assessment.page.assessedOnAfterToday"
    : undefined;
};

/** What is said where a definitive assessment has no Approver to go to. */
export const approverNeeded = (
  t: TFunction,
  caseManagerName: string | undefined,
): string =>
  caseManagerName === undefined
    ? t("assessment.approverNeeded.unnamed")
    : t("assessment.approverNeeded.named", { caseManager: caseManagerName });

/**
 * Confirms sending an assessment on, which cannot be undone, and asks for the
 * day its answers describe.
 *
 * Mounted for each opening, so it names the owner's Approver as at opening.
 * After a refusal it names the `track` approver instead where that differs,
 * or says nobody approves; the caller supplies who approves now.
 */
export const SubmitAssessmentDialog = ({
  track,
  onClose,
  onSubmit,
}: {
  track: SubmitTrack;
  onClose: () => Promisable<void>;
  onSubmit: (submission: Submission) => Promisable<SubmitOutcome>;
}) => {
  const { t } = useTranslation();
  const [assessedOn, setAssessedOn] = useState(today);
  const [triedToSubmit, setTriedToSubmit] = useState(false);
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<SubmitOutcome>();
  const current = track.kind === "approval" ? track.approver : undefined;
  const [named, setNamed] = useState(current);

  const approverChanged =
    track.kind === "approval" &&
    outcome === "refused" &&
    current?.userId !== named?.userId;
  const goesTo = approverChanged ? current : named;

  const parsedDay = IsoDateSchema.safeParse(assessedOn);
  const dayError = assessedOnError(parsedDay);
  const shownDayError = triedToSubmit ? dayError : undefined;

  const send = async () => {
    setTriedToSubmit(true);

    if (dayError !== undefined || !parsedDay.success) {
      return;
    }

    setNamed(goesTo);
    setOutcome(undefined);
    setSending(true);

    try {
      setOutcome(
        await onSubmit({
          assessedOn: parsedDay.data,
          approverUserId: goesTo?.userId,
        }),
      );
    } catch {
      setOutcome("failed");
    } finally {
      setSending(false);
    }
  };

  const nobodyToSendTo = track.kind === "approval" && goesTo === undefined;
  const leftUnanswered =
    track.kind === "to_asker" && track.notAnswered.count > 0
      ? track.notAnswered
      : undefined;

  return (
    <Dialog
      open
      // Nothing closes the dialog while a submission is on its way, so the
      // outcome is never lost.
      onClose={
        sending
          ? undefined
          : () => {
              void onClose();
            }
      }
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {match(track)
          .with({ kind: "approval" }, () => t("assessment.submitDialog.title"))
          .with({ kind: "completion" }, () =>
            t("assessment.completeDialog.title"),
          )
          .with({ kind: "to_asker" }, ({ askerName }) =>
            leftUnanswered === undefined
              ? t("assessment.sendDialog.title", { asker: askerName })
              : t("assessment.sendDialog.titleNotAnswered", {
                  asker: askerName,
                }),
          )
          .exhaustive()}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {approverChanged ? (
            <Alert severity="warning">
              {t("assessment.submitDialog.approverChanged")}
            </Alert>
          ) : null}
          {match(track)
            .with({ kind: "to_asker" }, () =>
              leftUnanswered === undefined ? (
                <>
                  <DialogContentText>
                    {t("assessment.sendDialog.body")}
                  </DialogContentText>
                  <DialogContentText>
                    {t("assessment.sendDialog.copyAfterSending")}
                  </DialogContentText>
                </>
              ) : (
                <>
                  <Alert severity="warning">
                    {t("assessment.sendDialog.notAnswered", {
                      count: leftUnanswered.count,
                      of: leftUnanswered.of,
                    })}
                    <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
                      {leftUnanswered.byDomain.map(
                        ({ group, title, count }) => (
                          <li key={group}>
                            {t("assessment.sendDialog.notAnsweredInDomain", {
                              domain: title,
                              count,
                            })}
                          </li>
                        ),
                      )}
                    </Box>
                  </Alert>
                  <DialogContentText>
                    {t("assessment.sendDialog.sentAsNotAnswered")}
                  </DialogContentText>
                </>
              ),
            )
            .with({ kind: "completion" }, ({ personName }) => (
              <DialogContentText>
                {t("assessment.completeDialog.body", { personName })}
              </DialogContentText>
            ))
            .with({ kind: "approval" }, ({ caseManagerName }) =>
              goesTo === undefined ? (
                <DialogContentText>
                  {approverNeeded(t, caseManagerName)}
                </DialogContentText>
              ) : (
                <>
                  <DialogContentText>
                    {t("assessment.submitDialog.goesTo", {
                      approver: goesTo.name,
                    })}
                  </DialogContentText>
                  <DialogContentText>
                    {t("assessment.submitDialog.next", {
                      approver: goesTo.name,
                    })}
                  </DialogContentText>
                </>
              ),
            )
            .exhaustive()}
          <TextField
            type="date"
            label={t("assessment.page.assessedOn")}
            helperText={
              shownDayError === undefined
                ? t("assessment.page.assessedOnHelp")
                : t(shownDayError)
            }
            error={shownDayError !== undefined}
            disabled={sending}
            value={assessedOn}
            onChange={(event) => {
              setAssessedOn(event.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ maxWidth: 240 }}
          />
          {(outcome === "refused" && !approverChanged) ||
          outcome === "failed" ? (
            <Alert severity="error">
              {/* A guest has no profile to go back to, so is told otherwise. */}
              {outcome === "refused" && track.kind === "to_asker"
                ? t("assessment.sendDialog.refused")
                : t(`assessment.page.submit.${outcome}`)}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <CancelOrProceedActions
        cancel={{
          label:
            leftUnanswered === undefined
              ? t("common.cancel")
              : t("assessment.sendDialog.goBack"),
          onClick: onClose,
          disabled: sending,
        }}
        proceed={{
          label: match(track)
            .with({ kind: "approval" }, () =>
              t("assessment.submitDialog.confirm"),
            )
            .with({ kind: "completion" }, () =>
              t("assessment.completeDialog.confirm"),
            )
            .with({ kind: "to_asker" }, ({ askerName }) =>
              leftUnanswered === undefined
                ? t("assessment.sendDialog.confirm", { asker: askerName })
                : t("assessment.sendDialog.confirmNotAnswered", {
                    count: leftUnanswered.count,
                  }),
            )
            .exhaustive(),
          onClick: send,
          disabled: sending || nobodyToSendTo,
          kind: "primary",
        }}
      />
    </Dialog>
  );
};
